import type { PaginatedResponse } from '@/types/api';
import type {
    Competency,
    CompetencyGroup,
    CompetencyStatusType,
} from '@/types/competency/Competency';

import { api } from '@/api/api';

export interface CompetencyGroupPayload {
    name: Record<string, string>;
    description: Record<string, string>;
    sortOrder: number;
    status: CompetencyStatusType;
}

export interface CompetencyPayload {
    competencyGroupId: string;
    name: Record<string, string>;
    description: Record<string, string>;
    sortOrder: number;
    status: CompetencyStatusType;
}

export interface CompetencyImpact {
    profilesAffected: { id: string; name: Record<string, string> }[];
    employeesAffectedCount: number;
}

export default {
    async getCompetencyGroups(
        page = 1,
        pageSize = 50,
    ): Promise<PaginatedResponse<CompetencyGroup>> {
        const response = await api.get(`/CompetencyGroup`, {
            params: { page, pageSize },
        });

        return response.data;
    },

    async createCompetencyGroup(payload: CompetencyGroupPayload): Promise<CompetencyGroup> {
        const response = await api.post(`/CompetencyGroup`, payload);

        return response.data;
    },

    async updateCompetencyGroup(
        id: string,
        payload: CompetencyGroupPayload,
    ): Promise<CompetencyGroup> {
        const response = await api.put(`/CompetencyGroup/${id}`, payload);

        return response.data;
    },

    async deleteCompetencyGroup(id: string, replacementGroupId?: string): Promise<CompetencyGroup> {
        const response = await api.delete(`/CompetencyGroup/${id}`, {
            params: replacementGroupId ? { replacementGroupId } : undefined,
        });

        return response.data;
    },

    async createCompetency(payload: CompetencyPayload): Promise<Competency> {
        const response = await api.post(`/Competency`, payload);

        return response.data;
    },

    async updateCompetency(
        id: string,
        payload: Omit<CompetencyPayload, 'competencyGroupId'>,
    ): Promise<Competency> {
        const response = await api.put(`/Competency/${id}`, payload);

        return response.data;
    },

    async getCompetencyImpact(id: string): Promise<CompetencyImpact> {
        const response = await api.get(`/Competency/${id}/impact`);

        return response.data;
    },

    async deactivateCompetency(id: string, replacementCompetencyId: string | null): Promise<void> {
        await api.post(`/Competency/${id}/deactivate`, { replacementCompetencyId });
    },

    async removeCompetency(id: string, replacementCompetencyId: string | null): Promise<void> {
        await api.post(`/Competency/${id}/remove`, { replacementCompetencyId });
    },

    async moveCompetency(id: string, targetCompetencyGroupId: string): Promise<Competency> {
        const response = await api.put(`/Competency/${id}/Move`, { targetCompetencyGroupId });

        return response.data;
    },

    async changeCompetencyStatus(id: string, status: CompetencyStatusType): Promise<Competency> {
        const response = await api.put(`/Competency/${id}/Status`, { status });

        return response.data;
    },

    async changeCompetencyGroupStatus(
        id: string,
        status: CompetencyStatusType,
    ): Promise<CompetencyGroup> {
        const response = await api.put(`/CompetencyGroup/${id}/Status`, { status });

        return response.data;
    },

    async removeCompetencyGroup(
        id: string,
        competencyReplacements: Record<string, string | undefined>,
    ): Promise<CompetencyGroup> {
        const response = await api.post(`/CompetencyGroup/${id}/remove`, {
            competencyReplacements,
        });

        return response.data;
    },

    async deactivateCompetencyGroup(
        id: string,
        competencyReplacements: Record<string, string | undefined>,
    ): Promise<CompetencyGroup> {
        const response = await api.post(`/CompetencyGroup/${id}/deactivate`, {
            competencyReplacements,
        });

        return response.data;
    },
};
